import { useState } from 'react';
import { Box, Button, MenuItem, Paper, Select, Stack, /* TextField, */ Typography } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDayjs } from '@mui/x-date-pickers/AdapterDayjs';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import ReplayIcon from '@mui/icons-material/Replay';
import SearchIcon from '@mui/icons-material/Search';

type DateRange = { from: Dayjs | null; to: Dayjs | null };

type Props = {
  value?: DateRange;
  onChange?: (range: DateRange) => void;
  onFilter?: (range: DateRange) => void;
  label?: string;
  showPresets?: boolean;
  minDate?: Dayjs | null;
  maxDate?: Dayjs | null;

};

export default function DateRangeSelector({
  value,
  onChange,
  onFilter,
  label = 'Rango de fechas',
  showPresets = true,
  minDate = null,
  maxDate = null,
}: Props) {
  const [selected, setSelected] = useState<string>('')
  const [range, setRange] = useState<DateRange>(() => ({
    from: value?.from ?? null,
    to: value?.to ?? null,
  }));

  const apply = (next: DateRange) => {
    setRange(next);
    if (!onFilter) {
      onChange?.(next);
    }

  };

  const presets = {
    'Hoy': { from: dayjs(), to: dayjs() },
    'Últimos 7 días': { from: dayjs().subtract(6, 'day'), to: dayjs() },
    'Este mes': { from: dayjs().startOf('month'), to: dayjs().endOf('month') },
    'Mes anterior': { from: dayjs().subtract(1, 'month').startOf('month'), to: dayjs().subtract(1, 'month').endOf('month') },
  } as Record<string, DateRange>;

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Paper sx={{ p: 2, borderRadius: 2 }} elevation={0}>
        <Stack spacing={1}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="subtitle1" style={{ fontWeight: 'bold' }}>{label}</Typography>
            {showPresets && (
              <Select
                size="small"
                value={selected}
                onChange={(e) => {
                  const key = e.target.value as string;
                  setSelected(key)
                  if (!key) return;
                  apply(presets[key]);

                }}
                displayEmpty
                renderValue={(value) => value || 'Períodos'}
              >
                <MenuItem value="">Períodos</MenuItem>
                {Object.keys(presets).map((k) => (
                  <MenuItem key={k} value={k}>{k}</MenuItem>
                ))}
              </Select>
            )}
          </Box>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems="center">
            <DatePicker
              label="Desde"
              value={range.from}
              onChange={(v) => apply({ ...range, from: v })}
              slotProps={{ textField: { fullWidth: true } }}
              disableFuture={false}
              minDate={minDate ?? undefined}
              maxDate={maxDate ?? undefined}
            />

            <DatePicker
              label="Hasta"
              value={range.to}
              onChange={(v) => apply({ ...range, to: v })}
              slotProps={{ textField: { fullWidth: true } }}
              disableFuture={false}
              minDate={minDate ?? undefined}
              maxDate={maxDate ?? undefined}
            />
          </Stack>

          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
            <Button
              size="small"
              onClick={() => apply({ from: null, to: null })}
              endIcon={<ReplayIcon />}
            >
              Limpiar
            </Button>
            <Button
              variant="contained"
              size="small"
              onClick={() => { onFilter?.(range); onChange?.(range); }}
              endIcon={<SearchIcon />}
            >
              Filtrar
            </Button>
          </Box>
        </Stack>
      </Paper>
    </LocalizationProvider>
  );
}
